import React, { useEffect, useState } from 'react';
import { Check, Loader2, Save } from 'lucide-react';

import { callingSttConfigAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from '../ui/use-toast';

// 2026-09-22 (Docs/CALLING_ELEVENLABS_STT_OPTION_PLAN.md): split out of
// CallingVoiceSettings.tsx (which is TTS/voice only) into its own
// component -- same "one component per concern" split
// CallingBehaviorSettings.tsx already used relative to that file. Rendered
// as its own section on the "AI Calling" settings page (see
// AICallingSettingsLayout.tsx). Backend: app/api/calling_stt_config.py,
// app/models/calling_stt_config.py (AccountCallingSTTConfig).
//
// 2026-09-22: simplified per explicit feedback -- dropped the "not yet
// verified for live-call latency" warning paragraph and the "no
// configuration needed" explainer paragraph (both read as clutter, not
// useful on a settings page), and the ElevenLabs key field is hidden
// entirely (not just pre-filled) whenever a key is already resolvable --
// see the key-reuse-row block below -- instead of always showing an input.
type CallingSttConfig = {
  streaming_enabled: boolean;
  provider?: string;
  deepgram_api_key?: string | null; // masked when returned from GET
  deepgram_model?: string | null;
  deepgram_endpointing_ms?: number | null;
  has_deepgram_key?: boolean;
  has_env_deepgram_key?: boolean;
  vad_endpointing_enabled?: boolean;
  // The PRIMARY/batch STT provider -- unrelated to streaming_enabled/
  // provider above, which is the separate real-time Deepgram add-on that
  // races against whichever batch_provider resolves. 'groq' (deployment
  // default, whisper-large-v3-turbo, unconfigurable model) or 'elevenlabs'
  // (scribe_v2, experimental -- not yet verified for live-call latency).
  batch_provider?: string;
  elevenlabs_api_key?: string | null; // masked when returned from GET
  elevenlabs_stt_model?: string | null;
  has_elevenlabs_key?: boolean;
  // Whether a TTS ElevenLabs key exists that batch_provider='elevenlabs'
  // would reuse if elevenlabs_api_key is left blank -- one ElevenLabs key
  // covers both their TTS and STT APIs, no reason to ask for it twice.
  has_reusable_tts_elevenlabs_key?: boolean;
};

const DEFAULT_STT_CONFIG: CallingSttConfig = {
  streaming_enabled: false,
  provider: 'deepgram',
  deepgram_api_key: '',
  deepgram_model: 'nova-2',
  deepgram_endpointing_ms: 300,
  vad_endpointing_enabled: false,
  batch_provider: 'groq',
  elevenlabs_api_key: '',
  elevenlabs_stt_model: 'scribe_v2',
};

export default function CallingSpeechToTextSettings() {
  const [sttConfig, setSttConfig] = useState<CallingSttConfig>(DEFAULT_STT_CONFIG);
  const [newDeepgramKey, setNewDeepgramKey] = useState('');
  const [newElevenLabsSttKey, setNewElevenLabsSttKey] = useState('');
  const [showElevenLabsSttKeyOverride, setShowElevenLabsSttKeyOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sttSaving, setSttSaving] = useState(false);
  const [sttError, setSttError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await callingSttConfigAPI.getConfig();
        if (!cancelled && data) setSttConfig({ ...DEFAULT_STT_CONFIG, ...data });
      } catch (err: any) {
        if (!cancelled) setSttError(err?.message || 'Failed to load speech-to-text settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSttSave = async () => {
    setSttSaving(true);
    setSttError(null);
    try {
      const payload = {
        streaming_enabled: sttConfig.streaming_enabled,
        // Only send a new key if the admin actually typed one — never
        // resend the masked placeholder we got back from GET.
        deepgram_api_key: newDeepgramKey || undefined,
        deepgram_model: sttConfig.deepgram_model || undefined,
        deepgram_endpointing_ms: sttConfig.deepgram_endpointing_ms || undefined,
        vad_endpointing_enabled: !!sttConfig.vad_endpointing_enabled,
        batch_provider: sttConfig.batch_provider || 'groq',
        elevenlabs_api_key: newElevenLabsSttKey || undefined,
        elevenlabs_stt_model: sttConfig.elevenlabs_stt_model || undefined,
        // 2026-10-04 (plan 1.2): which service streams -- Deepgram or ElevenLabs.
        streaming_provider: sttConfig.provider || 'deepgram',
      };
      const updated = await callingSttConfigAPI.updateConfig(payload);
      setSttConfig({ ...DEFAULT_STT_CONFIG, ...updated });
      setNewDeepgramKey('');
      setNewElevenLabsSttKey('');
      setShowElevenLabsSttKeyOverride(false);
      toast({ title: 'Saved', description: 'Speech-to-text settings updated.' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save settings';
      setSttError(typeof msg === 'string' ? msg : 'Failed to save settings');
      toast({ title: 'Save failed', description: String(msg), variant: 'destructive' as any });
    } finally {
      setSttSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading speech-to-text settings…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sttError && (
        <Alert variant="destructive">
          <AlertDescription>{sttError}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Primary provider</CardTitle>
          <CardDescription>Used to transcribe the caller's voice on every call turn.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setSttConfig((c) => ({ ...c, batch_provider: 'groq' }))}
              className={`rounded-xl border p-3.5 text-left transition-colors ${
                (sttConfig.batch_provider || 'groq') === 'groq'
                  ? 'border-primary bg-accent ring-1 ring-primary'
                  : 'border-border/60 hover:bg-accent/50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">Groq Whisper Turbo</span>
                <Badge variant="success" className="text-[10px] uppercase">
                  Current default
                </Badge>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                1.3–6.6s typical on a live call
              </p>
            </button>

            <button
              type="button"
              onClick={() => setSttConfig((c) => ({ ...c, batch_provider: 'elevenlabs' }))}
              className={`rounded-xl border p-3.5 text-left transition-colors ${
                sttConfig.batch_provider === 'elevenlabs'
                  ? 'border-primary bg-accent ring-1 ring-primary'
                  : 'border-border/60 hover:bg-accent/50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">ElevenLabs Scribe</span>
                <Badge variant="outline" className="text-[10px] uppercase text-muted-foreground">
                  Experimental
                </Badge>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Higher published accuracy on accents
              </p>
            </button>
          </div>

          {sttConfig.batch_provider === 'elevenlabs' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="el-stt-model">Model</Label>
                <Input
                  id="el-stt-model"
                  placeholder="scribe_v2"
                  value={sttConfig.elevenlabs_stt_model || ''}
                  onChange={(e) => setSttConfig((c) => ({ ...c, elevenlabs_stt_model: e.target.value }))}
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                {sttConfig.has_elevenlabs_key || showElevenLabsSttKeyOverride ? (
                  <>
                    <Label htmlFor="el-stt-api-key">ElevenLabs API key</Label>
                    <Input
                      id="el-stt-api-key"
                      type="password"
                      placeholder={sttConfig.elevenlabs_api_key || '••••••••'}
                      value={newElevenLabsSttKey}
                      onChange={(e) => setNewElevenLabsSttKey(e.target.value)}
                    />
                  </>
                ) : sttConfig.has_reusable_tts_elevenlabs_key ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
                    <span className="flex-1">Using the ElevenLabs key already on file (Voice section)</span>
                    <button
                      type="button"
                      className="shrink-0 font-medium text-primary underline"
                      onClick={() => setShowElevenLabsSttKeyOverride(true)}
                    >
                      Use a different key
                    </button>
                  </div>
                ) : (
                  <>
                    <Label htmlFor="el-stt-api-key">ElevenLabs API key</Label>
                    <Input
                      id="el-stt-api-key"
                      type="password"
                      placeholder="sk-..."
                      value={newElevenLabsSttKey}
                      onChange={(e) => setNewElevenLabsSttKey(e.target.value)}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {sttConfig.batch_provider === 'elevenlabs' && (
            <p className="border-t border-border/60 pt-4 text-xs text-muted-foreground">
              Always falls back to Groq Whisper if ElevenLabs errors or times out — not configurable,
              calls never go silent because of this choice.
            </p>
          )}

          <div className="flex justify-end border-t border-border/60 pt-4">
            <Button onClick={handleSttSave} disabled={sttSaving}>
              {sttSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-base">Streaming add-on</CardTitle>
            <CardDescription>
              Optional real-time transcription that races alongside the primary provider above —
              usually the fastest way to a reply.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label htmlFor="stt-streaming-enabled" className="text-sm text-muted-foreground">
              Enabled
            </Label>
            <Switch
              id="stt-streaming-enabled"
              checked={sttConfig.streaming_enabled}
              onCheckedChange={(checked) => setSttConfig((c) => ({ ...c, streaming_enabled: checked }))}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 2026-10-04 (Docs/AI_CALL_CENTER_PRODUCTION_READINESS_PLAN.md 1.2) */}
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'elevenlabs', label: 'ElevenLabs (Scribe Realtime)' },
              { value: 'deepgram', label: 'Deepgram' },
            ].map((option) => (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={(sttConfig.provider || 'deepgram') === option.value ? 'default' : 'outline'}
                onClick={() => setSttConfig((c) => ({ ...c, provider: option.value }))}
              >
                {option.label}
              </Button>
            ))}
          </div>

          {(sttConfig.provider || 'deepgram') === 'elevenlabs' ? (
            <p className="text-xs text-muted-foreground">
              Uses your ElevenLabs API key (this card or the Voice card) — nothing else to set up. The caller's
              chosen language is sent automatically.
            </p>
          ) : (
          <>
          {!sttConfig.has_deepgram_key && sttConfig.has_env_deepgram_key && (
            <Alert>
              <AlertDescription>
                A Deepgram key is already configured for the deployment — enabling this without
                entering your own key below will use that shared key.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="dg-api-key">Deepgram API key</Label>
              <Input
                id="dg-api-key"
                type="password"
                placeholder={sttConfig.has_deepgram_key ? sttConfig.deepgram_api_key || '••••••••' : 'Deepgram key'}
                value={newDeepgramKey}
                onChange={(e) => setNewDeepgramKey(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dg-model">Model</Label>
              <Input
                id="dg-model"
                placeholder="nova-2"
                value={sttConfig.deepgram_model || ''}
                onChange={(e) => setSttConfig((c) => ({ ...c, deepgram_model: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dg-endpointing">Endpointing (ms)</Label>
              <Input
                id="dg-endpointing"
                type="number"
                min={100}
                max={2000}
                value={sttConfig.deepgram_endpointing_ms ?? 300}
                onChange={(e) =>
                  setSttConfig((c) => ({ ...c, deepgram_endpointing_ms: Number(e.target.value) || 300 }))
                }
              />
            </div>
          </div>
          </>
          )}

          <div className="flex items-center justify-between gap-4 border-t border-border/60 pt-4">
            <div>
              <Label htmlFor="vad-endpointing-enabled" className="text-sm font-medium">
                Smarter endpointing (free)
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Real voice-activity detection instead of a simple volume check.
              </p>
            </div>
            <Switch
              id="vad-endpointing-enabled"
              checked={!!sttConfig.vad_endpointing_enabled}
              onCheckedChange={(checked) => setSttConfig((c) => ({ ...c, vad_endpointing_enabled: checked }))}
            />
          </div>

          <div className="flex justify-end border-t border-border/60 pt-4">
            <Button onClick={handleSttSave} disabled={sttSaving}>
              {sttSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
